import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { toast } from "sonner";

const DEFAULTS = {
  gpa: "3.92",
  current_college: "Valencia College",
  intended_major: "Economics",
  target_schools: "Harvard, Yale, UPenn, Cornell",
  strengths: "Strong macroeconomics coursework, Phi Theta Kappa officer, undergrad research with a UF professor.",
  weaknesses: "No quantitative competitions, limited summer experience outside Florida.",
  activities: "Phi Theta Kappa VP of Scholarship; founded a campus financial-literacy club (40+ members); tutor at the math lab; part-time at a local CPA office.",
};

export default function DiagnosticPoc() {
  const [form, setForm] = useState(DEFAULTS);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    try {
      const payload = {
        gpa: parseFloat(form.gpa),
        current_college: form.current_college,
        intended_major: form.intended_major,
        target_schools: form.target_schools
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        strengths: form.strengths,
        weaknesses: form.weaknesses,
        activities: form.activities,
      };
      const res = await api.post("/diagnostic/ivy-signals", payload);
      setResult(res.data);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Diagnostic failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 px-6 py-12" data-testid="diagnostic-poc-page">
      <div className="max-w-5xl mx-auto space-y-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-stone-500">
            Phase 0 spike · AI diagnostic
          </div>
          <h1 className="font-serif text-4xl text-stone-900 mt-2">
            Ivy Signals Diagnostic
          </h1>
          <p className="text-stone-600 mt-3 max-w-2xl">
            Verifies the Emergent LLM (gpt-5.4) wiring with a single structured-JSON call.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="border-stone-200">
            <CardHeader>
              <CardTitle className="text-stone-900">Student profile</CardTitle>
              <CardDescription>Fill in (pre-populated for quick testing).</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={onSubmit} className="space-y-4" data-testid="diagnostic-form">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="gpa">GPA</Label>
                    <Input
                      id="gpa"
                      data-testid="input-gpa"
                      value={form.gpa}
                      onChange={onChange("gpa")}
                      required
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="current_college">Current college</Label>
                    <Input
                      id="current_college"
                      data-testid="input-current-college"
                      value={form.current_college}
                      onChange={onChange("current_college")}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="intended_major">Intended major</Label>
                  <Input
                    id="intended_major"
                    data-testid="input-major"
                    value={form.intended_major}
                    onChange={onChange("intended_major")}
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="target_schools">Target schools (comma-separated)</Label>
                  <Input
                    id="target_schools"
                    data-testid="input-targets"
                    value={form.target_schools}
                    onChange={onChange("target_schools")}
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="strengths">Strengths</Label>
                  <Textarea
                    id="strengths"
                    data-testid="input-strengths"
                    rows={3}
                    value={form.strengths}
                    onChange={onChange("strengths")}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="weaknesses">Weaknesses</Label>
                  <Textarea
                    id="weaknesses"
                    data-testid="input-weaknesses"
                    rows={3}
                    value={form.weaknesses}
                    onChange={onChange("weaknesses")}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="activities">Activities</Label>
                  <Textarea
                    id="activities"
                    data-testid="input-activities"
                    rows={3}
                    value={form.activities}
                    onChange={onChange("activities")}
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading}
                  data-testid="diagnostic-submit-btn"
                >
                  {loading ? "Analyzing…" : "Run diagnostic"}
                </Button>
              </form>
            </CardContent>
          </Card>

          <Card className="border-stone-200">
            <CardHeader>
              <CardTitle className="text-stone-900">Result</CardTitle>
              <CardDescription>Structured JSON returned by the model.</CardDescription>
            </CardHeader>
            <CardContent>
              {!result && !loading && (
                <div className="text-sm text-stone-500" data-testid="diagnostic-empty">
                  Submit the form to see results.
                </div>
              )}
              {loading && (
                <div className="text-sm text-stone-500">Calling the model…</div>
              )}
              {result && (
                <div className="space-y-4" data-testid="diagnostic-result">
                  <Snippet
                    label="Strongest asset"
                    value={result.strongest_asset}
                    testid="result-strongest-asset"
                  />
                  <Snippet
                    label="Biggest narrative risk"
                    value={result.biggest_narrative_risk}
                    testid="result-risk"
                  />
                  <Snippet
                    label="Recommended next step"
                    value={result.recommended_next_step}
                    testid="result-next-step"
                  />
                  <div className="pt-2">
                    <div className="text-xs uppercase tracking-[0.18em] text-stone-500 mb-2">
                      Signals (6)
                    </div>
                    <ul className="space-y-2" data-testid="result-signals">
                      {result.signals.map((s) => (
                        <li
                          key={s.name}
                          className="border border-stone-200 bg-white rounded p-3"
                          data-testid={`signal-${s.name.replace(/\s+/g, "-").toLowerCase()}`}
                        >
                          <div className="flex justify-between items-center">
                            <span className="font-medium text-stone-900 text-sm">
                              {s.name}
                            </span>
                            <span className="text-sm font-mono text-stone-700">
                              {s.score}/10
                            </span>
                          </div>
                          <p className="text-sm text-stone-600 mt-1">{s.comment}</p>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Snippet({ label, value, testid }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.18em] text-stone-500 mb-1">
        {label}
      </div>
      <div className="text-sm text-stone-800" data-testid={testid}>
        {value}
      </div>
    </div>
  );
}
